import React from 'react';
import PropTypes from 'prop-types';
import ResponsiveTableContainer from './ResponsiveTableContainer';

/**
 * DynamicTable (Versión Optimizada para Fixed Layout)
 * Se eliminaron las clases de ancho (w-xx) de los presets porque
 * ahora el ancho lo controla estrictamente la prop 'width' en style={{}}.
 */
const DynamicTable = ({ columns, data, renderCard, size = 'M', getRowColor }) => {

  // =====================================================================
  // 1. SISTEMA DE DISEÑO (Presets Limpios)
  // Ya no definimos anchos aquí. Solo alineación y tipografía.
  // =====================================================================
  const columnPresets = {
    code: {
      headerClass: "whitespace-nowrap text-center", // Quitamos w-16
      cellClass: "font-mono text-gray-600 text-center"
    },

    text: { 
      headerClass: "text-left", // Quitamos w-auto
      cellClass: "whitespace-normal break-words leading-tight text-gray-800 font-medium"
    },

    secondary: { 
      headerClass: "text-left whitespace-nowrap", // Quitamos w-18
      cellClass: "text-gray-600 whitespace-normal leading-tight"
    },
    
    center: { 
      headerClass: "text-center", // Quitamos w-16
      cellClass: "text-center text-gray-600"
    },

    status: {
      headerClass: "text-center", // Quitamos w-24
      cellClass: "text-center" 
    },

    action: {
      headerClass: "text-center", // Quitamos w-16
      cellClass: "text-center"
    }
  };

  // =====================================================================
  // 2. ESTILOS BASE
  // =====================================================================
  const styles = {
    thead: "bg-green-700 text-white",
    thBase: "px-2 py-2 text-xs font-semibold align-middle",
    row: "border-b hover:bg-gray-100 align-top bg-white even:bg-gray-50", 
    tdBase: "px-2 py-2 text-xs align-top",
    loading: "text-center py-6 text-xs text-gray-500",
    
    card: {
      container: "bg-white rounded shadow p-3 border-l-4",
      header: "flex justify-between mb-1",
      title: "font-bold text-gray-800 text-sm mb-2",
      //grid: "grid grid-cols-2 gap-y-1 text-xs text-gray-600 mb-3",
      grid: "grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs text-gray-600 mb-3",
      footer: "flex justify-end gap-3 border-t pt-2"
    }
  };

  // =====================================================================
  // 3. TARJETA AUTOMÁTICA (Móvil)
  // =====================================================================
  const DefaultMobileCard = ({ row }) => {
    // Clasificación de Columnas
    const codeCol = columns.find(col => col.type === 'code');
    const statusCol = columns.find(col => col.type === 'status');
    const titleCol = columns.find(col => col.type === 'text');
    const actionCol = columns.find(col => col.type === 'action');
    
    // El resto a la grilla
    const gridCols = columns.filter(col => 
      col !== codeCol && col !== statusCol && col !== titleCol && col !== actionCol
    );

    const borderColorClass = getRowColor ? getRowColor(row) : 'border-gray-300';

    return (
      <div className={`${styles.card.container} ${borderColorClass}`}>
        <div className={styles.card.header}>
          {codeCol && (
            <span className="font-mono text-xs font-bold text-gray-500">
              {codeCol.render ? codeCol.render(row) : row[codeCol.accessor]}
            </span>
          )}
          {statusCol && (
            statusCol.render ? statusCol.render(row) : row[statusCol.accessor]
          )}
        </div>

        {titleCol && (
          <div className={styles.card.title}>
            {titleCol.render ? titleCol.render(row) : row[titleCol.accessor]}
          </div>
        )}

        <div className={styles.card.grid}>
          {gridCols.map((col, i) => (
            <div key={i}>
              <span className="font-bold">{col.header}:</span>{' '}
              {col.render ? col.render(row) : row[col.accessor]}
            </div>
          ))}
        </div>

        {actionCol && (
          <div className={styles.card.footer}>
            {actionCol.render(row)}
          </div>
        )}
      </div>
    );
  };

  // =====================================================================
  // 4. VISTA ESCRITORIO
  // =====================================================================
  const DesktopView = (
    <table 
      className="w-full text-sm text-left text-gray-500"
      style={{ tableLayout: "fixed", width: "100%" }} // Mantenemos lo que forzamos antes
    >
      <thead className={styles.thead}>
        <tr>
          {columns.map((col, index) => {
            const preset = columnPresets[col.type] || {};
            // Ya no hay conflicto de clases de ancho aquí
            const finalHeaderClass = `${styles.thBase} ${preset.headerClass || ''} ${col.className || ''}`;
            
            return (
              <th
                key={index}
                className={finalHeaderClass}
                style={col.width ? { width: col.width } : {}} // Protección si no envías width
              >
                {col.header}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {data && data.length > 0 ? (
          data.map((row, rowIndex) => (
            <tr key={row.id || rowIndex} className={styles.row}>
              {columns.map((col, colIndex) => {
                const preset = columnPresets[col.type] || {};
                const finalCellClass = `${styles.tdBase} ${preset.cellClass || ''} ${col.cellClass || ''}`;

                return (
                  <td key={`${rowIndex}-${colIndex}`} className={finalCellClass}>
                    {col.render ? col.render(row) : row[col.accessor]}
                  </td>
                );
              })}
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={columns.length} className={styles.loading}>
              No hay resultados.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  // =====================================================================
  // 5. RENDER FINAL
  // =====================================================================
  return (
    <ResponsiveTableContainer 
      size={size} 
      desktop={DesktopView} 
      mobile={<>
        {data && data.length > 0 ? (
          data.map((row, index) => (
            <React.Fragment key={row.id || index}>
              {renderCard ? renderCard(row) : <DefaultMobileCard row={row} />}
            </React.Fragment>
          ))
        ) : (
          <div className={styles.loading}>No hay resultados.</div>
        )}
      </>} 
    />
  );
};

DynamicTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  renderCard: PropTypes.func,
  size: PropTypes.string,
  getRowColor: PropTypes.func
};

export default DynamicTable;